import { Button } from "antd";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { ReactNode, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  EUserRoleName,
  InfoMessages,
  isGcProjectAdminRole
} from "../../constants";
import GridLoadingIndicator from "./grid-loading-indicator";

function isGcAdminRole(role: string | undefined) {
  return (
    isGcProjectAdminRole(role) ||
    role === EUserRoleName[EUserRoleName.gc_sc_project_admin]
  );
}

function shouldShowLoadingOverlay(
  submittalExists: number,
  submittalListLoading: boolean,
  delayAfterComponentInit: boolean
) {
  return (!submittalExists && submittalListLoading) || !delayAfterComponentInit;
}

function LoadingOverlay() {
  return (
    <div className="absolute z-50 left-0 right-0 top-0 bottom-0 flex justify-center items-center bg-opacity-70 bg-white pointer-events-none">
      <div>
        <GridLoadingIndicator />
      </div>
    </div>
  );
}

function CenteredStatusMessage({ children }: { children: ReactNode }) {
  return (
    <div className="absolute top-[45%] z-50 left-0 right-0 flex justify-center">
      {children}
    </div>
  );
}

function IntegrationSetupInstructions({
  projectId,
  onNavigate
}: {
  projectId: string;
  onNavigate: (path: string) => void;
}) {
  return (
    <div className="absolute top-[25%] z-50 left-0 right-0 flex justify-center text-base">
      <div className="max-w-[800px]">
        <div className="mb-4">
          <p className="font-semibold">Instructions: </p>
        </div>
        <div>
          <ul className="space-y-1.5 pl-5">
            <li>
              You have selected Integration Mode as the preferred Submittal
              Management Mode. To import submittals from another application,
              you will first have to configure the integration.
            </li>
            <li>
              Upon clicking on the button provided below, you will be redirected
              to configure the integration.
            </li>
            <li>
              After the above set up has been completed, submittals will be
              imported from the selected application once a day.
            </li>
          </ul>
        </div>
        <div className="mt-10 flex items-center justify-center">
          <Button
            type="text"
            size="small"
            className="workflow-document-upload mr-1"
            onClick={() => {
              onNavigate(`/project/${projectId}/settings/integrations`);
            }}
          >
            Click Here
          </Button>
          <span>to configure the integration.</span>
        </div>
      </div>
    </div>
  );
}

function resolveIntegrationModeContent({
  isIntegrationConfigured,
  isLoggedInUserGcAdmin,
  lastImportStatus,
  emptyImportLog,
  projectId,
  onNavigate
}: {
  isIntegrationConfigured: boolean;
  isLoggedInUserGcAdmin: boolean;
  lastImportStatus: string | undefined;
  emptyImportLog: boolean;
  projectId: string;
  onNavigate: (path: string) => void;
}): ReactNode {
  let sectionContents: ReactNode = null;

  if (!isIntegrationConfigured) {
    sectionContents = isLoggedInUserGcAdmin ? (
      <IntegrationSetupInstructions
        projectId={projectId}
        onNavigate={onNavigate}
      />
    ) : (
      <CenteredStatusMessage>
        Integration is yet to be performed by the GC.
      </CenteredStatusMessage>
    );
  }
  if (
    isIntegrationConfigured &&
    (!lastImportStatus ||
      (lastImportStatus && lastImportStatus === "PROCESSING"))
  ) {
    sectionContents = (
      <CenteredStatusMessage>
        {InfoMessages.ImportingSubmittals}
      </CenteredStatusMessage>
    );
  }
  if (
    isIntegrationConfigured &&
    lastImportStatus &&
    lastImportStatus === "FAILED"
  ) {
    sectionContents = (
      <CenteredStatusMessage>
        {InfoMessages.FailedImportMsg}
      </CenteredStatusMessage>
    );
  }
  if (emptyImportLog && isIntegrationConfigured) {
    sectionContents = (
      <CenteredStatusMessage>
        {InfoMessages.EmptyACCSubmittalLogs}
      </CenteredStatusMessage>
    );
  }

  return sectionContents;
}

export function GridStatusMessage(params: {
  isIntegrationMode: any;
  isIntegrationConfigured: any;
  submittalListLoading: boolean;
  submittalListData: any;
  submittalListLoadedOnce: boolean;
  projectId: string;
  /** When true, only show loading; AG Grid owns the empty state. */
  useAgGridEmptyState?: boolean;
}) {
  const {
    isIntegrationMode,
    isIntegrationConfigured,
    submittalListLoading,
    submittalListData,
    submittalListLoadedOnce,
    projectId,
    useAgGridEmptyState = false
  } = params;

  const [delayAfterComponentInit, setDelayAfterComponentInit] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setDelayAfterComponentInit(true);
    }, 100);
  }, []);

  const history = useHistory();

  const { tokenContents, lastImportLogData }: TProjectContext =
    useContext(ProjectContext);

  const isLoggedInUserGcAdmin = isGcAdminRole(tokenContents?.role);

  const submittalExists = submittalListData?.length;
  const lastImportStatus = lastImportLogData?.status;
  const emptyImportLog = !lastImportLogData;

  if (
    shouldShowLoadingOverlay(
      submittalExists,
      submittalListLoading,
      delayAfterComponentInit
    )
  ) {
    return <LoadingOverlay />;
  }

  if (submittalExists) return null;

  // Voided/Deleted logs use AG Grid's no-rows overlay instead of
  // integration empty-state copy (Import Log / setup instructions).
  if (useAgGridEmptyState) return null;

  if (isIntegrationMode) {
    return resolveIntegrationModeContent({
      isIntegrationConfigured,
      isLoggedInUserGcAdmin,
      lastImportStatus,
      emptyImportLog,
      projectId,
      onNavigate: history.push
    });
  }

  if (submittalListLoadedOnce && isLoggedInUserGcAdmin) {
    return (
      <CenteredStatusMessage>
        {InfoMessages.EmptySubmittalWorkflowMode}
      </CenteredStatusMessage>
    );
  }

  return null;
}
